import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import QueryString from 'query-string';
import _ from 'lodash';
import UrlHelper from '../../utils/urlHelper';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import BarChartIcon from '@mui/icons-material/BarChart';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import SearchIcon from '@mui/icons-material/Search';
import VideoCameraBackOutlinedIcon from '@mui/icons-material/VideoCameraBackOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import Loading from '../../components/shared/Loading';
import Page from '../Page';
import ExerciseEditorDialog from './ExerciseEditorDialog';
import DialogTitle from '../shared/DialogTitle';
import { useLazyQuery, useMutation } from '@apollo/client/react';
import ServerError from '../error/ServerError';
import { useSnackbar } from 'notistack';
import {
  CREATE_EXERCISE,
  DELETE_EXERCISE,
  GET_EXERCISES,
} from '../../graphql/exercise';
import config from '../../config';

const EQUIPMENT_LABELS = {
  0: 'Bodyweight',
  1: 'Free Weights',
  2: 'Gym Equipment',
  3: 'Other',
};

const equipmentFor = (exercise) =>
  EQUIPMENT_LABELS[exercise.equipment] ||
  exercise.tags?.find((tag) => /barbell|dumbbell|bodyweight/i.test(tag)) ||
  'Bodyweight';

const hasExerciseVideo = (exercise) =>
  Boolean(exercise.videos?.length || exercise.howtoVideos?.length || exercise.youtubeVideoUrl);

const getYoutubeEmbedUrl = (url) => {
  const youtubeVideoId = UrlHelper.extractYoutubeVideoIdFromYoutubeUrl(url);
  return youtubeVideoId ? `https://www.youtube.com/embed/${youtubeVideoId}` : null;
};

const NoExerciseMedia = () => (
  <Box
    sx={{
      alignItems: 'center',
      bgcolor: '#f1f5f9',
      color: '#64748b',
      display: 'flex',
      fontSize: 13,
      fontWeight: 700,
      height: '100%',
      justifyContent: 'center',
      width: '100%',
    }}
  >
    No media
  </Box>
);

const StatCard = ({ icon, value, label, color, backgroundColor }) => (
  <Card variant="outlined" sx={{ borderColor: '#e2e8f0', borderRadius: 1 }}>
    <CardContent sx={{ py: 2.25, '&:last-child': { pb: 2.25 } }}>
      <Stack direction="row" spacing={2} alignItems="center">
        <Box
          sx={{
            width: 46,
            height: 46,
            borderRadius: 1.5,
            color,
            backgroundColor,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {icon}
        </Box>
        <Box>
          <Typography sx={{ fontWeight: 800, fontSize: 22, lineHeight: 1 }}>
            {value}
          </Typography>
          <Typography sx={{ color: '#40536b', fontSize: 14 }}>{label}</Typography>
        </Box>
      </Stack>
    </CardContent>
  </Card>
);

const ExercisePreviewMedia = ({ exercise, kind = 'playback' }) => {
  const videoUrl =
    kind === 'howto' ? exercise.howtoVideos?.[0]?.url : exercise.videos?.[0]?.url;
  const imageUrl =
    kind === 'howto'
      ? exercise.howtoImages?.[0]?.url
      : exercise.coverImageUrl || exercise.images?.[0]?.url;
  const youtubeEmbedUrl =
    kind === 'howto' ? getYoutubeEmbedUrl(exercise.youtubeVideoUrl) : null;

  if (youtubeEmbedUrl) {
    return (
      <Box
        component="iframe"
        title={`${exercise.title} how-to video`}
        src={youtubeEmbedUrl}
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
        sx={{
          border: 0,
          width: '100%',
          aspectRatio: '16 / 9',
          borderRadius: 1,
          backgroundColor: '#111827',
        }}
      />
    );
  }

  if (videoUrl) {
    return (
      <Box
        component="video"
        src={videoUrl}
        controls
        playsInline
        muted={kind === 'playback'}
        sx={{
          width: '100%',
          aspectRatio: '16 / 9',
          objectFit: 'cover',
          borderRadius: 1,
          backgroundColor: '#111827',
        }}
      />
    );
  }

  if (imageUrl) {
    return (
      <Box
        component="img"
        src={imageUrl}
        alt={exercise.title}
        sx={{
          width: '100%',
          aspectRatio: '16 / 9',
          objectFit: 'cover',
          borderRadius: 1,
          backgroundColor: '#111827',
        }}
      />
    );
  }

  return (
    <Box
      sx={{
        alignItems: 'center',
        aspectRatio: '16 / 9',
        bgcolor: '#111827',
        borderRadius: 1,
        color: '#fff',
        display: 'flex',
        justifyContent: 'center',
      }}
    >
      <FitnessCenterIcon sx={{ fontSize: 56 }} />
    </Box>
  );
};

const ExercisePreviewDialog = ({ exercise, open, onClose }) => {
  if (!exercise) {
    return null;
  }

  const tags = exercise.tags || [];
  const hasHowto =
    exercise.howtoVideos?.length ||
    exercise.howtoImages?.length ||
    exercise.youtubeVideoUrl;

  return (
    <Dialog open={!!open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle onClose={onClose}>Exercise Preview</DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={3}>
          <Grid item xs={12} md={7}>
            <Stack spacing={2}>
              <ExercisePreviewMedia exercise={exercise} />
              <Box>
                <Typography sx={{ fontSize: 28, fontWeight: 800, color: '#071120' }}>
                  {exercise.title}
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
                  <Chip size="small" label={equipmentFor(exercise)} />
                  {tags.map((tag) => (
                    <Chip key={tag} size="small" label={tag} />
                  ))}
                </Stack>
              </Box>
              <Typography sx={{ color: '#40536b', whiteSpace: 'pre-wrap' }}>
                {exercise.desc || 'No description added.'}
              </Typography>
            </Stack>
          </Grid>
          <Grid item xs={12} md={5}>
            <Stack spacing={2.5}>
              <Box>
                <Typography sx={{ fontWeight: 800, fontSize: 16, mb: 1 }}>
                  Playback media
                </Typography>
                <Typography sx={{ color: '#506176', fontSize: 14 }}>
                  This is what athletes see during workout playback.
                </Typography>
              </Box>

              <Divider />

              <Box>
                <Typography sx={{ fontWeight: 800, fontSize: 16, mb: 1.5 }}>
                  How-to media
                </Typography>
                {hasHowto ? (
                  <ExercisePreviewMedia exercise={exercise} kind="howto" />
                ) : (
                  <Typography sx={{ color: '#506176', fontSize: 14 }}>
                    No how-to video has been added.
                  </Typography>
                )}
              </Box>
            </Stack>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

const ExerciseCard = ({ exercise, onEdit, onPreview, onDuplicate, onDelete }) => {
  const image = exercise.coverImageUrl || exercise.images?.[0]?.url;
  const tags = (exercise.tags || []).slice(0, 3);
  const equipment = equipmentFor(exercise);
  const hasVideo = hasExerciseVideo(exercise);

  return (
    <Card
      variant="outlined"
      sx={{
        borderColor: '#e2e8f0',
        borderRadius: 1,
        overflow: 'hidden',
        height: '100%',
        boxShadow: '0 8px 20px rgba(15, 23, 42, 0.04)',
      }}
    >
      <Box sx={{ position: 'relative', height: 164, backgroundColor: '#111827' }}>
        {image ? (
          <Box
            component="img"
            src={image}
            alt={exercise.title}
            sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        ) : (
          <NoExerciseMedia />
        )}
        {hasVideo && (
          <Box
            sx={{
              position: 'absolute',
              left: 12,
              bottom: 12,
              width: 34,
              height: 34,
              borderRadius: '50%',
              backgroundColor: 'rgba(0,0,0,0.65)',
              color: '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <PlayArrowIcon />
          </Box>
        )}
        <Typography
          sx={{
            position: 'absolute',
            right: 10,
            bottom: 10,
            color: '#fff',
            fontWeight: 800,
            backgroundColor: 'rgba(0,0,0,0.65)',
            borderRadius: 1,
            px: 0.75,
          }}
        >
          {hasVideo ? 'Video' : 'No video'}
        </Typography>
        <IconButton
          size="small"
          onClick={onDuplicate}
          sx={{
            position: 'absolute',
            top: 10,
            right: 10,
            backgroundColor: '#fff',
            borderRadius: 1,
            '&:hover': { backgroundColor: '#f8fafc' },
          }}
        >
          <MoreVertIcon fontSize="small" />
        </IconButton>
      </Box>
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        <Typography noWrap sx={{ fontWeight: 800, fontSize: 19, mb: 0.75 }}>
          {exercise.title}
        </Typography>
        <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
          {tags.map((tag) => (
            <Chip key={tag} size="small" label={tag} />
          ))}
          <Chip size="small" label={equipment} />
        </Stack>
        <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 2 }}>
          <VideoCameraBackOutlinedIcon sx={{ color: '#40536b', fontSize: 18 }} />
          <Typography sx={{ color: '#40536b', fontSize: 14 }}>
            {hasVideo ? 'Video available' : 'Missing video'}
          </Typography>
        </Stack>
        <Stack direction="row" spacing={1.25}>
          <Button fullWidth variant="outlined" startIcon={<EditOutlinedIcon />} onClick={onEdit}>
            Edit
          </Button>
          <Button
            fullWidth
            variant="outlined"
            startIcon={<VisibilityOutlinedIcon />}
            onClick={onPreview}
          >
            Preview
          </Button>
          <IconButton
            onClick={onDelete}
            sx={{ border: '1px solid #d0d9e6', borderRadius: 1, width: 42 }}
          >
            <MoreVertIcon fontSize="small" />
          </IconButton>
        </Stack>
      </CardContent>
    </Card>
  );
};

const ExerciseList = () => {
  const queryParams = QueryString.parse(window.location.search);
  const trainerId = queryParams.trainerId || null;
  const [searchQuery, setSearchQuery] = useState('');
  const [tagFilter, setTagFilter] = useState('ALL');
  const [equipmentFilter, setEquipmentFilter] = useState('ALL');
  const [mediaFilter, setMediaFilter] = useState('ALL');
  const [exerciseEdit, setExerciseEdit] = useState(null);
  const [exercisePreview, setExercisePreview] = useState(null);
  const [openExerciseEdit, setOpenExerciseEdit] = useState(false);
  const [openExerciseCreator, setOpenExerciseCreator] = useState(false);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(24);
  const { enqueueSnackbar } = useSnackbar();
  const [fetchExercises, { data, loading, error, refetch }] = useLazyQuery(GET_EXERCISES);
  const initialLoadRef = useRef(true);
  const delayedSearch = useCallback(
    _.debounce((value) => {
      fetchExercises({
        variables: { input: { limit: 100, trainerId, searchTerm: value } },
      });
    }, 500),
    [fetchExercises, trainerId]
  );

  useEffect(() => {
    delayedSearch(searchQuery);
  }, [searchQuery, delayedSearch]);

  useEffect(() => {
    if (!loading && data) {
      initialLoadRef.current = false;
    }
  }, [loading, data]);

  useEffect(() => {
    fetchExercises({
      variables: { input: { limit: 100, trainerId, searchTerm: '' } },
    });
  }, [fetchExercises, trainerId]);

  const [deleteExercise] = useMutation(DELETE_EXERCISE, {
    onCompleted: () => {
      refetch();
    },
    onError: (error) => {
      enqueueSnackbar(`Error deleting exercise: ${error.message}`, {
        variant: 'error',
      });
    },
  });
  const [createExercise] = useMutation(CREATE_EXERCISE, {
    onCompleted: () => {
      refetch();
    },
    onError: (error) => {
      enqueueSnackbar(`Error creating exercise: ${error.message}`, {
        variant: 'error',
      });
    },
  });

  const exercises = data?.exercises ?? [];
  const tagOptions = useMemo(
    () =>
      Array.from(
        new Set(exercises.flatMap((exercise) => exercise.tags || []).filter(Boolean))
      ).sort(),
    [exercises]
  );
  const equipmentOptions = useMemo(
    () =>
      Array.from(new Set(exercises.map((exercise) => equipmentFor(exercise)))).sort(),
    [exercises]
  );
  const filteredExercises = useMemo(
    () =>
      exercises.filter((exercise) => {
        const matchesSearch = (exercise.title ?? '')
          .toLowerCase()
          .includes(searchQuery.toLowerCase());
        const matchesTag =
          tagFilter === 'ALL' || (exercise.tags || []).includes(tagFilter);
        const matchesEquipment =
          equipmentFilter === 'ALL' || equipmentFor(exercise) === equipmentFilter;
        const matchesMedia =
          mediaFilter === 'ALL' ||
          (mediaFilter === 'VIDEO' && hasExerciseVideo(exercise)) ||
          (mediaFilter === 'MISSING_VIDEO' && !hasExerciseVideo(exercise));

        return matchesSearch && matchesTag && matchesEquipment && matchesMedia;
      }),
    [equipmentFilter, exercises, mediaFilter, searchQuery, tagFilter]
  );
  const totalPages = Math.max(1, Math.ceil(filteredExercises.length / perPage));
  const currentPage = Math.min(page, totalPages);
  const pageStart = filteredExercises.length === 0 ? 0 : (currentPage - 1) * perPage + 1;
  const pageEnd = Math.min(currentPage * perPage, filteredExercises.length);
  const paginatedExercises = filteredExercises.slice(pageStart - 1, pageEnd);

  React.useEffect(() => {
    setPage(1);
  }, [equipmentFilter, mediaFilter, perPage, searchQuery, tagFilter]);

  const handleDelete = (id) => {
    if (!window.confirm('Delete the item?')) {
      return;
    }
    if (!id) {
      console.error('handleClickDelete but id missing.');
      return;
    }
    enqueueSnackbar('Deleting...');
    deleteExercise({ variables: { id } });
  };

  const handleSaved = () => {
    setExerciseEdit(null);
    setOpenExerciseEdit(false);
    setOpenExerciseCreator(false);
    refetch();
  };

  const handleEdit = (exercise) => {
    setExerciseEdit(exercise);
    setOpenExerciseEdit(true);
  };

  const handleDuplicate = ({ exercise }) => {
    const input = _.cloneDeep(exercise);
    delete input._id;
    delete input.createdAt;
    input.title = `${input.title} COPY`;
    createExercise({ variables: { input } });
  };

  if (error) {
    return <ServerError message={error.message} />;
  }

  if (loading && !data) {
    return <Loading />;
  }

  if (!data?.exercises) {
    return <ServerError message={'Could not load exercises!'} />;
  }

  const haveVideo = exercises.filter(
    (exercise) =>
      exercise.videos?.length || exercise.howtoVideos?.length || exercise.youtubeVideoUrl
  ).length;
  const haveVideoPct =
    exercises.length === 0 ? 0 : Math.round((haveVideo / exercises.length) * 100);

  return (
    <Page title={`Exercises : ${config.appDisplayName}`}>
      <ExerciseEditorDialog
        key={exerciseEdit?._id ?? 'id'}
        onSaved={handleSaved}
        open={openExerciseEdit}
        onClose={() => setOpenExerciseEdit(false)}
        exercise={exerciseEdit ?? {}}
      />

      <ExerciseEditorDialog
        key="new"
        onSaved={handleSaved}
        open={openExerciseCreator}
        onClose={() => setOpenExerciseCreator(false)}
        exercise={{}}
      />

      <ExercisePreviewDialog
        exercise={exercisePreview}
        open={!!exercisePreview}
        onClose={() => setExercisePreview(null)}
      />

      <Container maxWidth={false}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 3 }}>
          <Box>
            <Typography sx={{ fontSize: 30, fontWeight: 800, color: '#071120', mb: 1 }}>
              Exercises
            </Typography>
            <Typography sx={{ color: '#40536b', fontSize: 16 }}>
              Manage your exercise library. Add new exercises, edit details, and organize by muscle groups and equipment.
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setOpenExerciseCreator(true)}
            sx={{
              backgroundColor: '#4338ca',
              px: 2.5,
              py: 1.25,
              '&:hover': { backgroundColor: '#3730a3' },
            }}
          >
            Create New Exercise
          </Button>
        </Stack>

        <Grid container spacing={2.5} sx={{ mb: 2 }}>
          <Grid item xs={12} md={3}>
            <StatCard
              icon={<FitnessCenterIcon />}
              value={exercises.length}
              label="Total Exercises"
              color="#4338ca"
              backgroundColor="#edeaff"
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <StatCard
              icon={<BarChartIcon />}
              value={filteredExercises.length}
              label="Matching Exercises"
              color="#0284c7"
              backgroundColor="#e0f2fe"
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <StatCard
              icon={<VideoCameraBackOutlinedIcon />}
              value={`${haveVideoPct}%`}
              label="Have Video"
              color="#16a34a"
              backgroundColor="#dcfce7"
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <StatCard
              icon={<VideoCameraBackOutlinedIcon />}
              value={Math.max(0, exercises.length - haveVideo)}
              label="Missing Video"
              color="#f97316"
              backgroundColor="#fff0df"
            />
          </Grid>
        </Grid>

        <Card variant="outlined" sx={{ borderColor: '#e2e8f0', borderRadius: 1 }}>
          <CardContent>
            <Stack
              direction={{ xs: 'column', lg: 'row' }}
              spacing={2}
              alignItems={{ xs: 'stretch', lg: 'center' }}
              sx={{ mb: 3 }}
            >
              <TextField
                size="small"
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                placeholder="Search exercises..."
                sx={{ minWidth: { xs: '100%', lg: 340 } }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: '#40536b' }} />
                    </InputAdornment>
                  ),
                }}
              />
              <Select
                size="small"
                value={tagFilter}
                onChange={(event) => setTagFilter(event.target.value)}
                sx={{ minWidth: 180 }}
              >
                <MenuItem value="ALL">Tag: All</MenuItem>
                {tagOptions.map((tag) => (
                  <MenuItem key={tag} value={tag}>
                    Tag: {tag}
                  </MenuItem>
                ))}
              </Select>
              <Select
                size="small"
                value={equipmentFilter}
                onChange={(event) => setEquipmentFilter(event.target.value)}
                sx={{ minWidth: 180 }}
              >
                <MenuItem value="ALL">Equipment: All</MenuItem>
                {equipmentOptions.map((equipment) => (
                  <MenuItem key={equipment} value={equipment}>
                    Equipment: {equipment}
                  </MenuItem>
                ))}
              </Select>
              <Select
                size="small"
                value={mediaFilter}
                onChange={(event) => setMediaFilter(event.target.value)}
                sx={{ minWidth: 180 }}
              >
                <MenuItem value="ALL">Media: All</MenuItem>
                <MenuItem value="VIDEO">Has video</MenuItem>
                <MenuItem value="MISSING_VIDEO">Missing video</MenuItem>
              </Select>
              <Box sx={{ flex: 1 }} />
              <Button
                onClick={() => {
                  setSearchQuery('');
                  setTagFilter('ALL');
                  setEquipmentFilter('ALL');
                  setMediaFilter('ALL');
                }}
              >
                Clear all
              </Button>
            </Stack>

            <Stack
              direction={{ xs: 'column', md: 'row' }}
              justifyContent="space-between"
              alignItems={{ xs: 'stretch', md: 'center' }}
              sx={{ mb: 2 }}
              spacing={2}
            >
              <Typography sx={{ fontWeight: 800, fontSize: 17 }}>
                {filteredExercises.length.toLocaleString()} exercises
              </Typography>
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Typography sx={{ color: '#40536b' }}>Sort by:</Typography>
                <Select size="small" value="recent" sx={{ minWidth: 180 }}>
                  <MenuItem value="recent">Recently Updated</MenuItem>
                </Select>
              </Stack>
            </Stack>

            <Grid container spacing={3}>
              {paginatedExercises.map((exercise, index) => (
                <Grid item xs={12} sm={6} lg={3} key={exercise._id}>
                  <ExerciseCard
                    exercise={exercise}
                    index={index}
                    onEdit={() => handleEdit(exercise)}
                    onPreview={() => setExercisePreview(exercise)}
                    onDelete={() => handleDelete(exercise._id)}
                    onDuplicate={() => handleDuplicate({ exercise })}
                  />
                </Grid>
              ))}
            </Grid>
          </CardContent>
        </Card>

        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ py: 3 }}>
          <Typography sx={{ color: '#40536b' }}>
            Showing {pageStart} to {pageEnd} of{' '}
            {filteredExercises.length.toLocaleString()} exercises
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            <Button
              disabled={currentPage === 1}
              onClick={() => setPage((value) => Math.max(1, value - 1))}
              variant="outlined"
            >
              ‹
            </Button>
            {Array.from({ length: totalPages }, (_, index) => index + 1).map((pageNumber) => (
              <Button
                key={pageNumber}
                onClick={() => setPage(pageNumber)}
                variant={pageNumber === currentPage ? 'contained' : 'outlined'}
                sx={{ minWidth: 36 }}
              >
                {pageNumber}
              </Button>
            ))}
            <Button
              disabled={currentPage === totalPages}
              onClick={() => setPage((value) => Math.min(totalPages, value + 1))}
              variant="outlined"
            >
              ›
            </Button>
          </Stack>
          <Stack direction="row" spacing={1.25} alignItems="center">
            <Typography sx={{ color: '#40536b' }}>Show</Typography>
            <Select
              size="small"
              value={perPage}
              onChange={(event) => setPerPage(Number(event.target.value))}
            >
              <MenuItem value={8}>8</MenuItem>
              <MenuItem value={12}>12</MenuItem>
              <MenuItem value={24}>24</MenuItem>
            </Select>
            <Typography sx={{ color: '#40536b' }}>per page</Typography>
          </Stack>
        </Stack>
      </Container>
    </Page>
  );
};

export default ExerciseList;
